import { useQuery } from "@tanstack/react-query";
import { Activity, BrainCircuit, CalendarDays, Crosshair, HeartPulse, ShieldAlert, Sparkles, Stethoscope } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

function list(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String).filter(Boolean) : [];
}

function statusLabel(value?: string | null) {
  if (value === "probable") return "provável";
  if (value === "working") return "em formulação";
  if (value === "confirmed") return "confirmado";
  return value || "em revisão";
}

export function PatientClinicalTwin({ patient }: { patient: any }) {
  const { data: hypotheses = [] } = useQuery({
    queryKey: ["diagnostic-hypotheses", patient.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("diagnostic_hypotheses")
        .select("*")
        .eq("patient_id", patient.id)
        .is("deleted_at", null)
        .order("confidence_0_100", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const primary = list(patient.primary_diagnoses);
  const tags = list(patient.tags);

  return (
    <div className="space-y-3">
      <section className="clinical-twin-hero">
        <div className="min-w-0">
          <div className="clinical-kicker">PATIENT DIGITAL TWIN · LONGITUDINAL FORMULATION</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold tracking-tight">Mapa clínico de {patient.social_name || patient.full_name}</h2>
            <Badge variant="outline" className="rounded-full">Aug–Sep 2026</Badge>
            <Badge variant="outline" className="rounded-full border-primary/30 bg-primary/10 text-primary">médico em controle</Badge>
          </div>
          <p className="mt-2 max-w-4xl text-sm leading-relaxed text-muted-foreground">{patient.current_complaint || "Sem queixa longitudinal consolidada."}</p>
        </div>
        <div className="clinical-twin-orbit" aria-hidden="true"><BrainCircuit className="h-6 w-6" /></div>
      </section>

      <div className="grid gap-3 xl:grid-cols-[1.15fr_.85fr]">
        <Card className="clinical-panel p-4">
          <div className="flex items-center justify-between gap-3">
            <div><div className="clinical-kicker">DIAGNOSTIC CONSTELLATION</div><div className="mt-1 font-semibold">Diagnósticos e formulações</div></div>
            <Crosshair className="h-4 w-4 text-primary" />
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {(hypotheses.length ? hypotheses : primary.map((diagnostic: string) => ({ diagnostic_label: diagnostic }))).map((h: any, index: number) => (
              <div key={h.id || index} className="clinical-diagnostic-node">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-medium leading-snug">{h.diagnostic_label}</div>
                  {h.confidence_0_100 != null ? <span className="clinical-confidence">{h.confidence_0_100}%</span> : null}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] text-muted-foreground">
                  {h.status ? <span className="rounded-full border px-2 py-0.5">{statusLabel(h.status)}</span> : null}
                  {h.cid11_code ? <span className="rounded-full border px-2 py-0.5">CID {h.cid11_code}</span> : null}
                  {h.review_required ? <span className="rounded-full border border-warning/30 bg-warning/10 px-2 py-0.5 text-warning">revisão médica</span> : null}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="clinical-panel p-4">
          <div className="clinical-kicker">SEVERITY SNAPSHOT · 22 AUG</div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <div className="clinical-score"><span>MADRS</span><strong>48</strong><small>/60</small></div>
            <div className="clinical-score"><span>SHAPS</span><strong>13</strong><small>/14</small></div>
            <div className="clinical-score"><span>YMRS</span><strong>10</strong><small>/60</small></div>
          </div>
          <div className="mt-4 rounded-xl border border-destructive/20 bg-destructive/5 p-3">
            <div className="flex items-center gap-2 text-sm font-medium"><ShieldAlert className="h-4 w-4 text-destructive" />Risco longitudinal</div>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{patient.suicide_risk || "Sem formulação de risco consolidada."}</p>
          </div>
        </Card>
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        <Card className="clinical-panel p-4">
          <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><div className="clinical-kicker">FORMULATION</div></div>
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{patient.diagnostic_hypotheses}</p>
        </Card>
        <Card className="clinical-panel p-4">
          <div className="flex items-center gap-2"><Activity className="h-4 w-4 text-primary" /><div className="clinical-kicker">SUBSTANCE COURSE</div></div>
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{patient.substance_use_history}</p>
        </Card>
        <Card className="clinical-panel p-4">
          <div className="flex items-center gap-2"><HeartPulse className="h-4 w-4 text-primary" /><div className="clinical-kicker">SAFETY ENVELOPE</div></div>
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{patient.safety_notes}</p>
        </Card>
      </div>

      <Card className="clinical-panel overflow-hidden">
        <div className="clinical-panel-head">
          <div><div className="clinical-kicker">CLINICAL STORYLINE</div><div className="mt-1 font-semibold">Eventos que mudaram o curso</div></div>
          <CalendarDays className="h-4 w-4 text-primary" />
        </div>
        <div className="clinical-storyline">
          <div className="clinical-story"><time>22 AGO</time><strong>Psicometria de gravidade</strong><span>MADRS 48/60 · SHAPS 13/14 · YMRS 10/60.</span></div>
          <div className="clinical-story"><time>02 SET</time><strong>Escetamina indeferida</strong><span>Spravato fora da DUT; reanálise considerada. EMTr previamente negada.</span></div>
          <div className="clinical-story clinical-story-risk"><time>06 SET</time><strong>Descompensação aguda</strong><span>Ruptura/conflito, escalada autodestrutiva e ideação suicida; detalhes incompletos exigem reavaliação dinâmica.</span></div>
          <div className="clinical-story"><time>20 SET</time><strong>Esquema informado</strong><span>Venlafaxina 150 mg · mirtazapina 15 mg · pramipexol 0,25 mg · clonazepam 2 mg · clonidina 0,3 mg.</span></div>
        </div>
      </Card>

      <div className="flex flex-wrap gap-1.5">{tags.map((tag) => <span key={tag} className="rounded-full border bg-muted/35 px-2.5 py-1 text-[10px] text-muted-foreground">{tag}</span>)}</div>
    </div>
  );
}
